/**
 * Shower toilets (shared/toilet.ts): pure view logic for the room card, the home-tile badge, the settings sheet,
 * per-user profiles and the state relay throttle. No React, no DOM: unit-tested in model.test.ts.
 */
import {
  TOILET_LEVELS,
  type ToiletActivity, type ToiletBrand, type ToiletCommand, type ToiletEntry, type ToiletLinkState, type ToiletSettingKey,
  type ToiletSettings, type ToiletState,
} from '@home/shared';

// ---------------------------------------------------------------- live view

export interface ToiletView {
  /** Freshest state: the phone's live one if newer than the server's report. */
  state: ToiletState | undefined;
  /** Who / which phone reported `state` (server report), null when it came from this phone just now. */
  seenBy: { at: number; device: string } | null;
  link: ToiletLinkState | undefined;
  connected: boolean;
  paired: boolean;
}

export function toiletView(t: ToiletEntry, live: ToiletState | undefined, link: ToiletLinkState | undefined): ToiletView {
  const r = t.report;
  const useLive = !!live && (!r || live.at >= r.at);
  const state = useLive ? live : r;
  return {
    state,
    seenBy: !useLive && r ? { at: r.at, device: r.device } : live ? { at: live.at, device: '' } : null,
    link,
    connected: link?.link === 'connected',
    paired: isPaired(t),
  };
}

export const isPaired = (t: Pick<ToiletEntry, 'pairing'>): boolean => !!(t.pairing.address || t.pairing.key);

/** The link line. GROHE connects *to* the phone, and only while the app is open in the room. */
export function linkLabel(brand: ToiletBrand, link: ToiletLinkState | undefined): string | null {
  if (!link) return null;
  switch (link.link) {
    case 'connected': return 'Connected';
    case 'connecting': return 'Connecting…';
    case 'pairing': return 'Pairing…';
    case 'off': return link.detail ?? 'Bluetooth is off';
    case 'error': return link.detail ?? 'Bluetooth error';
    case 'waiting':
    case 'unavailable':
      return brand === 'grohe' ? 'Open the app in the room — the toilet connects to your phone' : 'Not in range';
  }
}

export const ACTIVITY_LABEL: Record<ToiletActivity, string> = {
  idle: 'Idle',
  washing: 'Washing',
  drying: 'Drying',
  flushing: 'Flushing',
  cleaning: 'Cleaning the nozzle',
  descaling: 'Descaling',
  deodorising: 'Deodorising',
};

export type ToiletTone = 'alert' | 'caution' | 'positive' | undefined;

/** One-line status for the card head: errors first, then activity, occupancy, descaling. */
export function statusLine(state: ToiletState | undefined): { text: string; tone: ToiletTone } {
  if (!state) return { text: 'No data yet', tone: undefined };
  if (state.errors.length) return { text: state.errors.length > 1 ? `${state.errors.length} errors` : `Error ${state.errors[0]!.code}`, tone: 'alert' };
  if (state.activity !== 'idle') return { text: ACTIVITY_LABEL[state.activity], tone: 'positive' };
  if (state.descalingDue) return { text: 'Descaling due', tone: 'caution' };
  if (state.occupied) return { text: 'Occupied', tone: undefined };
  return { text: state.occupied === false ? 'Free' : 'Idle', tone: undefined };
}

/** "14:02" today, "Sat 14:02" this week, "3 Oct 14:02" before that (home time zone). */
export function seenLabel(atS: number, nowMs: number, timeZone: string): string {
  const d = new Date(atS * 1000);
  const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { timeZone, ...o }).format(d);
  const day = (ms: number) => new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
  const time = fmt({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  if (day(atS * 1000) === day(nowMs)) return time;
  if (nowMs - atS * 1000 < 6 * 86_400_000) return `${fmt({ weekday: 'short' })} ${time}`;
  return `${fmt({ day: 'numeric', month: 'short' })} ${time}`;
}

/** "14:02 · Nat's iPhone". */
export function lastSeen(v: ToiletView, nowMs: number, timeZone: string): string | null {
  if (!v.seenBy) return null;
  const t = seenLabel(v.seenBy.at, nowMs, timeZone);
  return v.seenBy.device ? `${t} · ${v.seenBy.device}` : t;
}

// ---------------------------------------------------------------- controls

/** A control / setting key the model reports (`supports`); GROHE reports none, so everything shows. */
export function supports(state: ToiletState | undefined, key: string): boolean {
  const list = state?.supports;
  if (!list) return true;
  return list.includes(key) || list.includes(key.split('.')[0]!);
}

export interface ToiletControl { key: string; label: string; icon: string; command: ToiletCommand; confirm?: boolean }

export function washControls(state: ToiletState | undefined): ToiletControl[] {
  const all: ToiletControl[] = [
    { key: 'wash.rear', label: 'Rear', icon: 'droplet', command: { action: 'wash', mode: 'rear' } },
    { key: 'wash.front', label: 'Front', icon: 'droplet', command: { action: 'wash', mode: 'front' } },
    { key: 'wash.mild', label: 'Mild', icon: 'droplet', command: { action: 'wash', mode: 'mild' } },
    { key: 'dry', label: 'Dry', icon: 'wind', command: { action: 'dry' } },
  ];
  return all.filter((c) => supports(state, c.key));
}

export function flushControls(state: ToiletState | undefined): ToiletControl[] {
  if (!supports(state, 'flush')) return [];
  return [
    { key: 'flush.full', label: 'Full flush', icon: 'water-pump', command: { action: 'flush', size: 'full' } },
    { key: 'flush.eco', label: 'Eco flush', icon: 'leaf', command: { action: 'flush', size: 'eco' } },
  ];
}

/** GROHE: open / seat up / close; Duravit SensoWash: one toggle. */
export function lidControls(brand: ToiletBrand, state: ToiletState | undefined): ToiletControl[] {
  if (!supports(state, 'lid')) return [];
  if (brand === 'duravit') return [{ key: 'lid.toggle', label: 'Lid', icon: 'toilet', command: { action: 'lid', op: 'toggle' } }];
  return [
    { key: 'lid.open', label: 'Open', icon: 'chevron-up', command: { action: 'lid', op: 'open' } },
    { key: 'lid.seat', label: 'Seat', icon: 'toilet', command: { action: 'lid', op: 'seat' } },
    { key: 'lid.close', label: 'Close', icon: 'chevron-down', command: { action: 'lid', op: 'close' } },
  ];
}

export function careControls(state: ToiletState | undefined): ToiletControl[] {
  const all: ToiletControl[] = [
    { key: 'nozzleClean', label: 'Clean nozzle', icon: 'sparkle', command: { action: 'nozzleClean' } },
    { key: 'descale', label: 'Descale', icon: 'refresh', command: { action: 'descale' }, confirm: true },
  ];
  return all.filter((c) => supports(state, c.key));
}

// ---------------------------------------------------------------- settings

export const SETTING_LABEL: Record<ToiletSettingKey, string> = {
  seatTemp: 'Seat temperature',
  waterTemp: 'Water temperature',
  dryerTemp: 'Dryer temperature',
  sprayStrength: 'Spray strength',
  sprayPosition: 'Spray position',
  oscillation: 'Massage / oscillation',
  nightLight: 'Night light',
  deodoriser: 'Deodoriser',
  powerSaving: 'Power saving',
  buzzer: 'Beeps',
  autoFlush: 'Auto flush',
  autoLid: 'Auto lid',
  freezeProofing: 'Freeze protection',
};
const LEVEL_KEYS: ToiletSettingKey[] = ['seatTemp', 'waterTemp', 'dryerTemp', 'sprayStrength', 'sprayPosition'];
const FLAG_KEYS: ToiletSettingKey[] = ['oscillation', 'nightLight', 'deodoriser', 'powerSaving', 'buzzer', 'autoFlush', 'autoLid', 'freezeProofing'];

/** A setting shows when the toilet reports its value or lists it in `supports`. */
export function settingShown(state: ToiletState | undefined, key: ToiletSettingKey): boolean {
  if (!state) return false;
  return state.settings[key] !== undefined || !!state.supports?.includes(key);
}

/** Highest step of a level setting on this toilet (its own `levels`, e.g. Duravit 0–3), else the shared default. */
export function levelMax(state: ToiletState | undefined, key: ToiletSettingKey): number {
  return state?.levels?.[key] ?? TOILET_LEVELS[key] ?? 5;
}

export function levelSettings(state: ToiletState | undefined): Array<{ key: ToiletSettingKey; max: number }> {
  return LEVEL_KEYS.filter((k) => settingShown(state, k)).map((k) => ({ key: k, max: levelMax(state, k) }));
}
export function flagSettings(state: ToiletState | undefined): ToiletSettingKey[] {
  return FLAG_KEYS.filter((k) => settingShown(state, k));
}

/** Level readout: temperatures at 0 are off. */
export function levelLabel(key: ToiletSettingKey, v: number, max: number): string {
  if (v === 0 && (key === 'seatTemp' || key === 'waterTemp' || key === 'dryerTemp')) return 'Off';
  return `${v} / ${max}`;
}

// ---------------------------------------------------------------- profiles

/** A named set of preferred settings, per user (kept on this device: there is no server prefs API yet). */
export interface ToiletProfile { name: string; settings: ToiletSettings }

export function parseProfiles(raw: string | null): ToiletProfile[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    return v.flatMap((p) => {
      if (!p || typeof p !== 'object') return [];
      const { name, settings } = p as { name?: unknown; settings?: unknown };
      if (typeof name !== 'string' || !name.trim() || !settings || typeof settings !== 'object') return [];
      const clean: ToiletSettings = {};
      for (const [k, val] of Object.entries(settings as Record<string, unknown>)) {
        if (!(k in SETTING_LABEL)) continue;
        if (LEVEL_KEYS.includes(k as ToiletSettingKey) && typeof val === 'number' && Number.isFinite(val)) (clean as Record<string, unknown>)[k] = val;
        if (FLAG_KEYS.includes(k as ToiletSettingKey) && typeof val === 'boolean') (clean as Record<string, unknown>)[k] = val;
      }
      return [{ name: name.trim().slice(0, 40), settings: clean }];
    });
  } catch {
    return [];
  }
}

/** Save (or replace by name, case-insensitively) a profile. */
export function upsertProfile(list: ToiletProfile[], p: ToiletProfile): ToiletProfile[] {
  const i = list.findIndex((x) => x.name.toLowerCase() === p.name.toLowerCase());
  return i < 0 ? [...list, p] : list.map((x, j) => (j === i ? p : x));
}

/** The `set` commands that bring this toilet to the profile: only settings it has, only ones that differ, clamped. */
export function profileCommands(p: ToiletProfile, state: ToiletState | undefined): ToiletCommand[] {
  if (!state) return [];
  const out: ToiletCommand[] = [];
  for (const [k, v] of Object.entries(p.settings) as Array<[ToiletSettingKey, number | boolean]>) {
    if (!settingShown(state, k)) continue;
    const value = typeof v === 'number' ? Math.max(0, Math.min(levelMax(state, k), Math.round(v))) : v;
    if (state.settings[k] === value) continue;
    out.push({ action: 'set', key: k, value });
  }
  return out;
}

/** The settings worth saving into a profile (what the toilet reports). */
export function profileFrom(name: string, state: ToiletState | undefined): ToiletProfile {
  return { name: name.trim().slice(0, 40), settings: { ...(state?.settings ?? {}) } };
}

// ---------------------------------------------------------------- relay throttle

/** Fields whose change is news (sent at once); anything else waits for the throttle window. */
export function significantChange(prev: ToiletState | undefined, next: ToiletState): boolean {
  if (!prev) return true;
  return prev.activity !== next.activity
    || !!prev.occupied !== !!next.occupied
    || !!prev.descalingDue !== !!next.descalingDue
    || JSON.stringify(prev.errors.map((e) => e.code)) !== JSON.stringify(next.errors.map((e) => e.code))
    || JSON.stringify(prev.settings) !== JSON.stringify(next.settings);
}

export const RELAY_MIN_MS = 15_000;

/** When to POST a state the phone just heard: 'now', or the delay until the trailing send. */
export function relayDecision(lastSent: { at: number; state: ToiletState } | undefined, next: ToiletState, nowMs: number, minMs = RELAY_MIN_MS): 'now' | number {
  if (!lastSent) return 'now';
  if (significantChange(lastSent.state, next)) return 'now';
  const wait = lastSent.at + minMs - nowMs;
  return wait <= 0 ? 'now' : wait;
}

// ---------------------------------------------------------------- home tile

/** Toilets in these rooms (a tile speaks for its room and its child spaces, the ensuites). */
export function toiletsInRooms(roomIds: readonly string[], toilets: readonly ToiletEntry[]): ToiletEntry[] {
  const rooms = new Set(roomIds);
  return toilets.filter((t) => rooms.has(t.roomId));
}

/** Badge for one toilet: error > descaling due > occupied; nothing when all is well. */
export function badgeFor(state: ToiletState | undefined): { text: string; tone: ToiletTone } | null {
  if (!state) return null;
  if (state.errors.length) return { text: `Toilet · ${state.errors[0]!.text || `error ${state.errors[0]!.code}`}`, tone: 'alert' };
  if (state.descalingDue) return { text: 'Toilet · descale due', tone: 'caution' };
  if (state.occupied) return { text: 'Occupied', tone: undefined };
  return null;
}

export const BRAND_LABEL: Record<ToiletBrand, string> = { grohe: 'GROHE Sensia Arena', duravit: 'Duravit SensoWash' };
